import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from 'ui';
import React, { ReactNode } from 'react';

export interface ConfirmDialogProps {
  /**
   * Button label for the reject action. Default is "Cancel"
   */
  cancelText?: string;
  /**
   * Content of the ConfirmDialog. Strings are displayed as heading04.
   */
  children: ReactNode;
  /**
   * Button label for the accept action. Default is "Confirm"
   */
  continueText?: string;
  /**
   * If true, renders a danger button
   */
  isDestructive?: boolean;
  /**
   * If true, the ConfirmDialog is open
   */
  open: boolean;
  /**
   * Callback fired upon rejection
   */
  onCancel(): void;
  /**
   * Callback fired upon acceptance
   */
  onContinue(value?: unknown): void;
  /**
   * Title of the ConfirmDialog. Default is "Are you sure?"
   */
  title?: string;
}

/**
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const ConfirmDialog: React.FC<React.PropsWithChildren<ConfirmDialogProps>> = ({
  children,
  cancelText = 'Cancel',
  continueText = 'Confirm',
  isDestructive = false,
  open,
  onCancel,
  onContinue,
  title = 'Are you sure?',
}) => {
  const confirmButtonDisplayProps: Pick<ButtonProps, 'variant' | 'color'> = isDestructive
    ? { color: 'danger', variant: 'contained' }
    : { color: 'primary', variant: 'contained' };

  return (
    <Dialog
      aria-labelledby="confirmTitle"
      open={open}
      data-testid="confirmation-dialog"
      PaperProps={{
        sx: {
          width: 600,
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle id="confirmTitle" onClose={onCancel}>
        {title}
      </DialogTitle>
      <DialogContent>
        {typeof children === 'string' ? (
          <Typography variant="body1Medium" data-testid="defaultTypography">
            {children}
          </Typography>
        ) : (
          <>{children}</>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onCancel} size="large" variant="text">
          {cancelText}
        </Button>
        <Button
          data-testid="ContinueButton"
          onClick={onContinue}
          size="large"
          {...confirmButtonDisplayProps}
        >
          {continueText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
